import { Modal, Form, Button, InputGroup, Spinner } from "react-bootstrap";
import "../../assets/scss/new-entity-modal.scss";
import Select from "react-select";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import { useAuth } from "../../hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { queryActions } from "../../utils/reactQueryActions";
import { useFormik, FormikProvider } from "formik";
import { useEffect } from "react";
import * as yup from "yup";
import CreatableSelect from "react-select/creatable";
import ItemSizeDialog from "../ItemSizeDialog";
import { toast } from "react-toastify";
import { useStoreActions, useStoreState } from "easy-peasy";

import {
  nanoid,
  fetchActionsUtil,
  formatNumberSystem,
  atcTypeOptions,
} from "../../utils/helpers";
import CachedIcon from "mdi-react/CachedIcon";
import { useCurrencies, useUnits } from "../../utils/hooks";
import { isEmpty } from "lodash";

const getSubCategories = (parentCat = "All", subCategories = []) => {
  subCategories = subCategories
    .filter(el => el.Cat_Name === parentCat)
    .map(el => ({ label: el.Sub_Cat, value: el.Sub_Cat }));
  return [{ label: "All", value: "All" }, ...subCategories];
};

export default function NewATCModal({
  showCreateNewATCModal,
  setShowCreateNewATCModal,
  refetch,
  thenewItem,
}) {
  const { backendUrl, token } = useAuth();
  const queryClient = useQueryClient();
  const { deploymentCurrencies: currenciesOptions } = useCurrencies();

  const itemMeasurements = useStoreState(state => state.itemMeasurements);
  const setItemMeasurements = useStoreActions(
    actions => actions.setItemMeasurements
  );
  const generalSettings = useStoreState(state => state?.generalSettings);
  const { Units, getUnitsByUomById } = useUnits();

  const setUp = async () => {
    const [response, { storage }] = await Promise.all([
      fetch(`${backendUrl}/api/items/set-up?withVendorBalance=true`, {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      }),
      fetchActionsUtil(`${backendUrl}/api/items/storage-location`, "GET"),
    ]);

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message);
    }

    const { data } = await response.json();

    data.itemMeasurement = data?.itemMeasurement.map(el => ({
      label: el.Size,
      value: el.Size,
    }));

    data.categories = data.categories.map(el => ({
      value: el.Cat_Name,
      label: el.Cat_Name,
    }));

    data.products = data.products.map(el => ({
      value: el.Product_Name,
      label: el.Product_Name,
    }));

    data.storage = storage?.map(el => ({
      value: el.storageID,
      label: el.name,
    }));

    if (!isEmpty(data.systemNumber)) {
      formik.setFieldValue(
        "Bar_Code",
        formatNumberSystem(
          data.systemNumber?.numberPrefix,
          data.systemNumber?.currentNumber
        )
      );
      formik.setFieldValue("numberPrefix", data.systemNumber?.numberPrefix);
      formik.setFieldValue("currentNumber", data.systemNumber?.currentNumber);
    } else {
      formik.setFieldValue("Bar_Code", nanoid(6, "number"));
    }

    return data;
  };

  const {
    data = { itemMeasurement: [], categories: [], products: [] },
    refetch: refetchSetup,
    isLoading,
  } = useQuery([queryActions.ITEMS_SETUP], () => setUp(), {
    enabled: true,
    keepPreviousData: false,
  });

  const formik = useFormik({
    initialValues: {
      numberPrefix: "",
      currentNumber: "",
      Item_Type: "Inventory",
      Bar_Code: thenewItem?.Bar_Code ? thenewItem.Bar_Code : "",
      Item_Name: thenewItem?.Item_Name ? thenewItem.Item_Name : "",
      Size: "",
      Unit: "Bags",
      Product_Name: "",
      Cat_Name: "",
      Sub_Cat: "All",
      Branch: "HQ",
      currency: generalSettings?.prevailingCurrency,
      conversionAmount: generalSettings.dollarInBaseCurrency,
      uomId: "",
      Region: "",
      Plant: "",
      Depot: "",
      Date_Log: new Date(),
      Quantity: 0,
      Usage: "Sales",
      deliveryType: "",
    },
    validationSchema: yup.object().shape({
      Bar_Code: yup.string().required("Item Code is required"),
      Item_Name: yup.string().required("Item Name is required"),
      Product_Name: yup.string().required("required"),
      Cat_Name: yup.string().required("required"),
      Unit: yup.string().required(),
      deliveryType: yup.string().required("Delivery Type is required"),
    }),
    onSubmit: async values => {
      if (["Tons", "Pieces"].includes(values.Unit) && isEmpty(values.Size)) {
        return toast.error(
          `Tons and Pieces is not allowed here, select another unit`
        );
      }

      // Select plant or depot
      if (values.Plant === "" && values.Depot === "") {
        formik.setFieldError("Plant", "Please select Plant or Depot");
        formik.setFieldError("Depot", "Please select Plant or Depot");
        return;
      }

      values.Item_Desc = `${values.Region} / ${values.Plant || values.Depot}`;

      const payload = {
        newItem: values,
        currencyCode: values.currency,
        conversionAmount: values.conversionAmount,
      };

      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      createMutation.mutate(formData);
    },
  });

  useEffect(() => {
    if (thenewItem?.Bar_Code) {
      formik.setFieldValue("Bar_Code", thenewItem.Bar_Code);
    }
    if (thenewItem?.Item_Name) {
      formik.setFieldValue("Item_Name", thenewItem.Item_Name);
    }
  }, [thenewItem]);

  const createMutation = useMutation(
    async payload => {
      const url = `${backendUrl}/api/items/add-stock`;

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        body: payload,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message);
      }

      return response.json();
    },
    {
      onSuccess: ({ message }) => {
        toast.success(message);
        formik.resetForm();
        if (refetch) refetch();
        setShowCreateNewATCModal(false);
      },
      onError: ({ message = "" }) => {
        toast.error(`Unable to perform action: ${message}`);
      },
    }
  );

  const addItemMeasurement = async () => {
    const newSize = await ItemSizeDialog({});
    if (newSize) {
      queryClient.setQueryData([queryActions.ITEMS_SETUP], data => {
        data.itemMeasurement = [
          ...data.itemMeasurement,
          { value: newSize.Size, label: newSize.Size },
        ];
        return data;
      });
      formik.setFieldValue("Size", newSize.Size);
      setItemMeasurements([...itemMeasurements, newSize]);
    }
  };

  const fetchSetUpData = async () => {
    const response = await fetch(`${backendUrl}/api/incentive/set-up`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message);
    }
    const { data } = await response.json();

    data.plantSelect = data.plant.map(el => ({
      region: el.Region,
      label: el.Plant,
      value: el.Plant,
    }));

    data.depotSelect = data.depot.map(el => ({
      region: el.Region,
      label: el.Depot,
      value: el.Depot,
    }));

    data.regionSelect = data.region.map(el => ({
      label: el.region,
      value: el.region,
    }));

    return data;
  };

  const {
    data: incentiveData = {
      plantSelect: [],
      depotSelect: [],
      regionSelect: [],
      subCategoriesWithParent: [],
    },
  } = useQuery(["INCENTIVE_SETUP"], () => fetchSetUpData(), {
    keepPreviousData: true,
  });

  const filterByRegion = (region = "", data = []) => {
    return data.filter(el => el.region === region);
  };

  return (
    <Modal
      show={showCreateNewATCModal}
      onHide={() => setShowCreateNewATCModal(false)}
      size="lg"
      centered={true}
      animation={false}
      enforceFocus={false}
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <h1 title="Sales Inventory" className="h4">
            New SKU{" "}
            <Button
              onClick={() => refetchSetup()}
              variant=""
              className="text-primary"
            >
              <CachedIcon />
            </Button>
          </h1>
          <p>Add a new SKU by filling in the following forms.</p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormikProvider value={formik}>
          <Form
            noValidate
            onSubmit={formik.handleSubmit}
            className=""
            autoComplete="off"
          >
            <div className="">
              <div className="row">
                <Form.Group className="col-6 mb-3 pb-2">
                  <Form.Label className="mb-1">Item Code</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Enter Item Code"
                      name="Bar_Code"
                      value={formik.values.Bar_Code}
                      onChange={formik.handleChange}
                      isInvalid={
                        formik.touched.Bar_Code && !!formik.errors.Bar_Code
                      }
                      disabled={isLoading}
                    />
                    {isLoading && (
                      <InputGroup.Text>
                        <Spinner
                          animation="border"
                          variant="primary"
                          size="sm"
                        />
                      </InputGroup.Text>
                    )}
                  </InputGroup>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.Bar_Code}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="col-6 mb-3 pb-2">
                  <Form.Label className="mb-1">Item Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Item Name"
                    name="Item_Name"
                    value={formik.values.Item_Name}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.Item_Name && !!formik.errors.Item_Name
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.Item_Name}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="col-4 mb-3 pb-2">
                  <Form.Label className="mb-1">
                    Manufacturer / Product
                  </Form.Label>
                  <CreatableSelect
                    classNamePrefix={`form-select`}
                    options={data.products}
                    value={data?.products?.find(
                      el => el.value === formik.values.Product_Name
                    )}
                    onChange={({ value }) =>
                      formik.setFieldValue("Product_Name", value)
                    }
                    placeholder="Select Manufacturer"
                  />
                  {formik.touched.Product_Name && formik.errors.Product_Name ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Product_Name}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="col-4 mb-3 pb-2">
                  <Form.Label className="mb-1">Category</Form.Label>
                  <CreatableSelect
                    classNamePrefix={`form-select`}
                    options={data.categories}
                    value={data?.categories?.find(
                      el => el.value === formik.values.Cat_Name
                    )}
                    onChange={({ value }) => {
                      formik.setFieldValue("Cat_Name", value);
                      formik.setFieldValue("Sub_Cat", "All");
                    }}
                    placeholder="Select Category"
                  />
                  {formik.touched.Cat_Name && formik.errors.Cat_Name ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Cat_Name}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="col-4 mb-3 pb-2">
                  <Form.Label className="mb-1">Sub Category</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={getSubCategories(
                      formik.values.Cat_Name,
                      incentiveData?.subCategoriesWithParent
                    )}
                    value={
                      getSubCategories(
                        formik.values.Cat_Name,
                        incentiveData?.subCategoriesWithParent
                      ).find(el => el.value === formik.values.Sub_Cat) || ""
                    }
                    onChange={({ value }) =>
                      formik.setFieldValue("Sub_Cat", value)
                    }
                    className={
                      formik.touched.Sub_Cat && !!formik.errors.Sub_Cat
                        ? "is-invalid"
                        : ""
                    }
                    placeholder="Sub Category"
                  />
                  {formik.touched.Sub_Cat && !!formik.errors.Sub_Cat ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Sub_Cat}
                    </span>
                  ) : null}
                </Form.Group>
              </div>

              <hr />

              <Form.Group className="mb-3 pb-2">
                <Form.Label className="mb-1">Region</Form.Label>
                <Select
                  classNamePrefix={"form-select"}
                  options={incentiveData?.regionSelect}
                  className={
                    formik.touched.Region && !!formik.errors.Region
                      ? "is-invalid"
                      : ""
                  }
                  isSearchable={false}
                  value={
                    (incentiveData?.regionSelect || []).find(
                      el => el.value === formik.values.Region
                    ) || ""
                  }
                  onChange={selected => {
                    formik.setFieldValue("Region", selected.value);
                  }}
                  placeholder={"Select Region"}
                />
                {formik.touched.Region && !!formik.errors.Region ? (
                  <span className="text-danger mt-2">
                    {formik.errors.Region}
                  </span>
                ) : null}
              </Form.Group>

              <div className="row">
                <Form.Group className="col-6 mb-3 pb-2">
                  <Form.Label className="mb-1">Plant</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={filterByRegion(
                      formik.values.Region,
                      incentiveData?.plantSelect
                    )}
                    className={
                      formik.touched.Plant && !!formik.errors.Plant
                        ? "is-invalid"
                        : ""
                    }
                    isSearchable={false}
                    value={
                      (
                        filterByRegion(
                          formik.values.Region,
                          incentiveData?.plantSelect
                        ) || []
                      ).find(el => el.value === formik.values.Plant) || ""
                    }
                    onChange={selected => {
                      formik.setFieldValue("Plant", selected.value);
                      formik.setFieldValue("Depot", "");
                    }}
                    placeholder={
                      !isEmpty(formik.values.Region)
                        ? "Select Plant"
                        : "Select Region First"
                    }
                  />
                  {formik.touched.Plant && !!formik.errors.Plant ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Plant}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="col-6 mb-3 pb-2">
                  <Form.Label className="mb-1">Depot</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={filterByRegion(
                      formik.values.Region,
                      incentiveData?.depotSelect
                    )}
                    className={
                      formik.touched.Depot && !!formik.errors.Depot
                        ? "is-invalid"
                        : ""
                    }
                    isSearchable={false}
                    value={
                      (
                        filterByRegion(
                          formik.values.Region,
                          incentiveData?.depotSelect
                        ) || []
                      ).find(el => el.value === formik.values.Depot) || ""
                    }
                    onChange={selected => {
                      formik.setFieldValue("Depot", selected.value);
                      formik.setFieldValue("Plant", "");
                    }}
                    placeholder={
                      !isEmpty(formik.values.Region)
                        ? "Select Depot"
                        : "Select Region First"
                    }
                  />
                  {formik.touched.Depot && !!formik.errors.Depot ? (
                    <span className="text-danger mt-2">
                      {formik.errors.Depot}
                    </span>
                  ) : null}
                </Form.Group>
              </div>

              <div className="row text-nowrap align-items-center">
                <Form.Group className="col-5 mb-3 pb-2">
                  <Form.Label className="mb-1">UOM</Form.Label>
                  <Select
                    classNamePrefix={`form-select`}
                    options={
                      formik.values.uomId
                        ? getUnitsByUomById({ uomId: formik.values.uomId })
                        : Units
                    }
                    value={(formik.values.uomId
                      ? getUnitsByUomById({ uomId: formik.values.uomId })
                      : Units
                    ).find(el => el.value === formik.values.Unit)}
                    onChange={selected => {
                      formik.setFieldValue("Unit", selected?.value);
                      if (!["Tons", "Pieces"].includes(selected?.value)) {
                        formik.setFieldValue("Size", "");
                      }
                    }}
                  />
                  {formik.touched.Unit && !!formik.errors.Unit ? (
                    <span className="custom-invalid-feedback">
                      {formik.errors.Unit}
                    </span>
                  ) : null}
                </Form.Group>

                <Form.Group className="col-4 mb-3 pb-2">
                  <Form.Label className="mb-1">Delivery type</Form.Label>
                  <Select
                    classNamePrefix={"form-select"}
                    options={atcTypeOptions}
                    value={{
                      label: formik.values?.deliveryType,
                      value: formik.values?.deliveryType,
                    }}
                    onChange={selected => {
                      formik.setFieldValue("deliveryType", selected?.value);
                    }}
                    placeholder={"Select Delivery type"}
                    menuPlacement="top"
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </FormikProvider>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button
          onClick={() => setShowCreateNewATCModal(false)}
          variant="outline-primary"
          className="mt-3 px-4"
        >
          Cancel
        </Button>
        <Button
          onClick={() => formik.submitForm()}
          variant="primary"
          type="submit"
          className="submit-btn mt-3 px-4"
          disabled={createMutation.isLoading}
        >
          {createMutation.isLoading ? "Please wait..." : "Post"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
